// The stage-1 data as names every formula can use (design 02 12.1): the
// table 거래, the dimensions 사람·월·분류·결제수단 and the tensors
// 지출[사람, 월, 분류], 수입[사람, 월, 분류], 예산[월, 분류]. Computed here
// from the rows on this device (with my waiting changes), the same way as
// the views (views.ts): a category counts to its top-level one (대분류), a
// row without an item (or with one unknown here) goes under "(없음)", a
// top-level category's budget is its own or else its children's sum.
// 월 runs from the first month to the last (this month at least) with no
// month left out (monthRange), so that shift and predtrend count months.
// The dimensions people made (design 02 3.2) come too, by their names:
// their living items in order, or "1".."N" for a range; and the input
// tensors (3.1) over them, by their names, a cell whose place is gone
// (a deleted item, past a range's end) left out.
// The engine takes at most CALC_ELEMS_MAX elements in one tensor.
import type { CalcCommand, CalcValue } from '../calc/protocol'
import type { Id, TensorBudget, TensorDef, TensorDim, TensorEntry, TensorItem } from '../api'
import { customDims, dimItemNames } from '../dims'
import { COORD_SEP, customDimId, dimKeyName, entryMonths, inputTensors } from '../tensors'

/** the engine's limit on one array (calc.h CALC_MAX_ELEMS) */
export const CALC_ELEMS_MAX = 4_000_000
import { addMonths } from '../format'
import type { RowFields } from '../model'
import { NONE, budgetOf, itemsOf, topOf } from '../views'

export const LEDGER_COLUMNS = ['날짜', '구분', '금액', '사람', '분류', '대분류', '결제수단', '메모'] as const

export type BuiltinRow = Pick<RowFields, 'kind' | 'day' | 'amount' | 'person_item' | 'category_item' | 'payment_item' | 'memo'>

/** "2026-10-05" -> Excel's serial (1900 system). */
export function daySerial(day: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!m) return null
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const days = Math.round(ms / 86400000) + 25569
  return days >= 61 ? days : days - 1 // before 1900-03-01: Excel's 1900-02-29
}

/** Today and now in Korea as Excel serials. */
export function kstClock(now: number = Date.now()): { today: number; now: number } {
  const kst = now + 9 * 3600_000
  const days = Math.floor(kst / 86400000)
  const today = days + 25569
  return { today, now: today + (kst - days * 86400000) / 86400000 }
}

/**
 * The 월 dimension: every month from the first to the last one given, none
 * left out. A month without rows is still a month: shift(지출, 월, 1) of
 * October is September even when nothing was spent in September, and
 * predtrend counts it as 0 (it was not skipped).
 */
export function monthRange(months: Iterable<string>): string[] {
  let first = ''
  let last = ''
  for (const m of months) {
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(m)) continue
    if (first === '' || m < first) first = m
    if (last === '' || m > last) last = m
  }
  if (first === '') return []
  const out: string[] = []
  for (let m = first; m <= last; m = addMonths(m, 1)) out.push(m)
  return out
}

function pathOf(items: Record<number, TensorItem>, id: Id | null): string | null {
  const it = id ? items[id] : undefined
  if (!it) return null
  const parent = it.parent_id ? items[it.parent_id] : undefined
  return parent ? `${parent.name}>${it.name}` : it.name
}

function nameOf(items: Record<number, TensorItem>, id: Id | null): string | null {
  return id ? (items[id]?.name ?? null) : null
}

export interface BuiltinData {
  dims: { 사람: string[]; 월: string[]; 분류: string[]; 결제수단: string[] }
  /** the dimensions people made: their names and items */
  custom: { name: string; items: string[] }[]
  /** the input tensors: names, dimensions' names and values (row-major, null for none) */
  inputs: { name: string; dims: string[]; values: (number | null)[] }[]
  /** the 거래 table, in LEDGER_COLUMNS order */
  table: CalcValue[][]
  지출: number[]
  수입: number[]
  예산: number[]
}

/** What else a workbook has for formulas: the dimensions people made and
    the input tensors with their cells. */
export interface BuiltinMore {
  dims?: Record<number, TensorDim>
  tensors?: Record<number, TensorDef>
  entries?: Record<number, Record<string, TensorEntry>>
}

/** The names' data from the rows, items and budgets. */
export function builtinData(
  rows: readonly BuiltinRow[],
  items: Record<number, TensorItem>,
  budgets: Record<string, TensorBudget>,
  thisMonth: string,
  more: BuiltinMore = {},
): BuiltinData {
  const people = itemsOf(items, 'person').map((i) => i.name)
  const cats = itemsOf(items, 'category').map((i) => i.name)
  const pays = itemsOf(items, 'payment').map((i) => i.name)
  const add = (list: string[], v: string) => {
    if (!list.includes(v)) list.push(v)
  }
  const months = new Set<string>([thisMonth])
  for (const b of Object.values(budgets)) if (b.amount > 0) months.add(b.month)
  const tensors = inputTensors(more.tensors)
  for (const m of entryMonths(tensors, more.entries)) months.add(m)
  const table: CalcValue[][] = []
  const facts: { p: string; m: string; c: string; kind: string; amount: number }[] = []
  for (const r of rows) {
    const month = r.day.slice(0, 7)
    months.add(month)
    const person = nameOf(items, r.person_item)
    const top = topOf(items, r.category_item)
    const topName = top ? (items[top]?.name ?? null) : null
    table.push([
      daySerial(r.day),
      r.kind === 'income' ? '수입' : '지출',
      r.amount,
      person,
      pathOf(items, r.category_item),
      topName,
      nameOf(items, r.payment_item),
      r.memo === '' ? null : r.memo,
    ])
    const p = person ?? NONE
    const c = topName ?? NONE
    add(people, p)
    add(cats, c)
    const pay = nameOf(items, r.payment_item)
    if (pay) add(pays, pay)
    facts.push({ p, m: month, c, kind: r.kind, amount: r.amount })
  }
  // a tensor needs one item at least along each dimension
  if (people.length === 0) people.push(NONE)
  if (cats.length === 0) cats.push(NONE)
  const monthList = monthRange(months)
  const P = people.length
  const M = monthList.length
  const C = cats.length
  const pi = new Map(people.map((v, i) => [v, i]))
  const mi = new Map(monthList.map((v, i) => [v, i]))
  const ci = new Map(cats.map((v, i) => [v, i]))
  const spend = new Array<number>(P * M * C).fill(0)
  const income = new Array<number>(P * M * C).fill(0)
  for (const f of facts) {
    const at = ((pi.get(f.p) ?? 0) * M + (mi.get(f.m) ?? 0)) * C + (ci.get(f.c) ?? 0)
    if (f.kind === 'income') income[at]! += f.amount
    else spend[at]! += f.amount
  }
  const budget = new Array<number>(M * C).fill(0)
  const tops = itemsOf(items, 'category')
  monthList.forEach((m, k) => {
    for (const top of tops) {
      let b = budgetOf(budgets, m, top.id)
      if (b === 0) for (const child of itemsOf(items, 'category', top.id)) b += budgetOf(budgets, m, child.id)
      const c = ci.get(top.name)
      if (c !== undefined) budget[k * C + c] = b
    }
  })
  const custom = customDims(more.dims).map((d) => ({ name: d.name, items: dimItemNames(d, items) }))

  // input tensors: each cell's place as an index along the engine's items
  const at = (list: string[]) => new Map(list.map((v, i) => [v, i]))
  const payIdx = at(pays)
  const itemIndex = (role: TensorItem['role'], index: Map<string, number>, dimId = 0, top = false) => (part: string) => {
    const it = items[Number(part)]
    if (!it || it.deleted || it.role !== role || (dimId && it.dim_id !== dimId) || (top && it.parent_id)) return -1
    return index.get(it.name) ?? -1
  }
  const inputs: BuiltinData['inputs'] = []
  for (const t of tensors) {
    const names: string[] = []
    const lens: number[] = []
    const indexers: ((part: string) => number)[] = []
    for (const key of t.dims) {
      const name = dimKeyName(key, more.dims)
      if (name === null) break
      names.push(name)
      if (key === 'person') {
        lens.push(P)
        indexers.push(itemIndex('person', pi))
      } else if (key === 'category') {
        lens.push(C)
        indexers.push(itemIndex('category', ci, 0, true))
      } else if (key === 'payment') {
        lens.push(pays.length)
        indexers.push(itemIndex('payment', payIdx))
      } else if (key === 'month') {
        lens.push(M)
        indexers.push((part) => mi.get(part) ?? -1)
      } else {
        const d = more.dims![customDimId(key)]!
        const list = custom.find((c) => c.name === d.name)?.items ?? []
        lens.push(list.length)
        if (d.size !== null) indexers.push((part) => (/^[1-9]\d*$/.test(part) && Number(part) <= d.size! ? Number(part) - 1 : -1))
        else indexers.push(itemIndex('custom', at(list), d.id))
      }
    }
    if (names.length !== t.dims.length) continue // a dimension of it is gone
    const total = lens.reduce((a, b) => a * b, 1)
    if (total > CALC_ELEMS_MAX) continue
    const values = new Array<number | null>(total).fill(null)
    for (const e of Object.values(more.entries?.[t.id] ?? {})) {
      if (e.value === null) continue
      const parts = e.coord.split(COORD_SEP)
      if (parts.length !== indexers.length) continue
      let flat = 0
      let ok = true
      for (let k = 0; k < parts.length && ok; k++) {
        const i = indexers[k]!(parts[k]!)
        ok = i >= 0
        flat = flat * lens[k]! + i
      }
      if (ok) values[flat] = e.value
    }
    inputs.push({ name: t.name, dims: names, values })
  }
  return {
    dims: { 사람: people, 월: monthList, 분류: cats, 결제수단: pays },
    custom,
    inputs,
    table,
    지출: spend,
    수입: income,
    예산: budget,
  }
}

/**
 * The worker commands that set them (no recalculation: the caller adds one).
 * `prev`: what was set before, so that what is gone goes from the worker
 * too (a dimension people deleted or renamed).
 */
export function builtinCommands(d: BuiltinData, prev?: BuiltinData | null): CalcCommand[] {
  const now = new Set(d.custom.map((c) => c.name))
  const gone = (prev?.custom ?? []).filter((c) => !now.has(c.name)).map((c) => ({ name: c.name, items: null }))
  return [
    {
      cmd: 'set_dims',
      dims: [
        { name: '사람', items: d.dims.사람 },
        { name: '월', items: d.dims.월 },
        { name: '분류', items: d.dims.분류 },
        { name: '결제수단', items: d.dims.결제수단 },
        ...d.custom.map((c) => ({ name: c.name, items: c.items })),
        ...gone,
      ],
      recalc: false,
    },
    { cmd: 'set_table', name: '거래', columns: [...LEDGER_COLUMNS], rows: d.table, recalc: false },
    { cmd: 'set_tensor', name: '지출', dims: ['사람', '월', '분류'], values: d.지출, recalc: false },
    { cmd: 'set_tensor', name: '수입', dims: ['사람', '월', '분류'], values: d.수입, recalc: false },
    { cmd: 'set_tensor', name: '예산', dims: ['월', '분류'], values: d.예산, recalc: false },
    ...d.inputs.map((t): CalcCommand => ({ cmd: 'set_tensor', name: t.name, dims: t.dims, values: t.values, recalc: false })),
    ...(prev?.inputs ?? [])
      .filter((t) => !d.inputs.some((x) => x.name === t.name))
      .map((t): CalcCommand => ({ cmd: 'set_tensor', name: t.name, dims: null, recalc: false })),
  ]
}
